import React, { useEffect, useState } from 'react';
import { X, Loader, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFavoritesStore } from '../stores/favoritesStore';
import { parseReference } from '../utils/parseReference'; // Assuming the parseReference function is in this file

interface MoodResponseModalProps {
  isOpen: boolean;
  onClose: () => void;
  mood: string | null;
  verses: Array<{
    reference: string;
    text: string;
    reason: string;
  }> | null;
  loading: boolean;
  error: string | null;
}

export function MoodResponseModal({
  isOpen,
  onClose,
  mood,
  verses,
  loading,
  error
}: MoodResponseModalProps) {
  const navigate = useNavigate();
  const { toggleFavorite, isFavorite } = useFavoritesStore();
  const [favoriteStates, setFavoriteStates] = useState<Record<string, boolean>>({});

  useEffect(() => {
    // Check favorite status when verses change
    const checkFavorites = async () => {
      if (verses) {
        const newFavoriteStates: Record<string, boolean> = {};
        for (const verse of verses) {
          const [bookAndChapter, verseNum] = verse.reference.split(':');
          const [book, chapter] = bookAndChapter.split(' ');
          const verseId = `${book.toLowerCase()}_${chapter}_${verseNum}`;
          newFavoriteStates[verseId] = await isFavorite(verseId);
        }
        setFavoriteStates(newFavoriteStates);
      }
    };
    
    checkFavorites();
  }, [verses, isFavorite]);

  if (!isOpen) return null;

  const handleVerseClick = (reference: string) => {
    const { book, chapter, verse } = parseReference(reference);
    if (book && chapter) {
      const formattedBook = book.toLowerCase().replace(/\s+/g, '-');
      onClose();
      navigate(`/read/KJV/${formattedBook}/${chapter}`, {
        state: { verse }
      });
    }
  };

  const handleFavoriteClick = async (e: React.MouseEvent, verse: { reference: string; text: string }) => {
    e.stopPropagation(); // Prevent navigation when clicking the favorite button
    
    const [bookAndChapter, verseNum] = verse.reference.split(':');
    const [book, chapter] = bookAndChapter.split(' ');
    const verseId = `${book.toLowerCase()}_${chapter}_${verseNum}`;
    
    await toggleFavorite({
      id: verseId,
      book,
      chapter: parseInt(chapter),
      verse: parseInt(verseNum),
      text: verse.text,
      version: 'KJV'
    });

    // Toggle the local favorite state
    setFavoriteStates(prev => ({
      ...prev,
      [verseId]: !prev[verseId]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center p-4 mt-16 sm:mt-0 sm:items-center overflow-y-auto">
      <div className="bg-gray-900 rounded-xl w-full max-w-3xl max-h-[calc(100vh-5rem)] sm:max-h-[80vh] overflow-hidden shadow-xl transform transition-all my-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">
            Scriptures for {mood && mood.charAt(0).toUpperCase() + mood.slice(1)} Moments
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-white/60 hover:text-white transition-colors rounded-lg hover:bg-white/5"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(80vh-4rem)]">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-12">
              <Loader className="animate-spin text-gold-400" size={24} />
              <span className="text-white/80">Finding relevant scriptures...</span>
            </div>
          ) : error ? (
            <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/20">
              <p className="text-red-400">{error}</p>
            </div>
          ) : verses && verses.length > 0 ? (
            <div className="space-y-4">
              {verses.map((verse, index) => (
                <div
                  key={index}
                  onClick={() => handleVerseClick(verse.reference)}
                  className="bg-black/40 border border-gold-500/20 rounded-lg p-4 cursor-pointer hover:bg-black/60 transition-colors group"
                  data-tutorial="verse-actions"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-grow">
                      <h3 className="text-lg font-medium text-gold-400 mb-2">{verse.reference}</h3>
                      <p className="text-white/80 text-base leading-relaxed mb-2">{verse.text}</p>
                      <p className="text-white/60 text-sm">{verse.reason}</p>
                    </div>
                    <button
                      onClick={(e) => handleFavoriteClick(e, verse)}
                      className="p-2 text-white/40 hover:text-gold-400 transition-colors rounded-lg hover:bg-white/5"
                      aria-label="Toggle favorite"
                    >
                      <Heart
                        size={20}
                        className={favoriteStates[`${verse.reference.split(':').shift().split(' ').join('_').toLowerCase()}_${verse.reference.split(':').pop()}`] ? 'fill-gold-400 text-gold-400' : ''}
                      />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-white/60">No scriptures found. Please try again.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
